import { Box, CircularProgress, IconButton } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import MenuItem from '@material-ui/core/MenuItem';
import TableContainer from '@material-ui/core/TableContainer';
import TuneIcon from '@material-ui/icons/Tune';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import Select from '@material-ui/core/Select';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import moment from 'moment';
import { GetApp } from '@material-ui/icons';
import _ from 'lodash';
import { SearchBar, MenuFiltersOptions } from './tableComponents';
import { searchTableValue } from '../../../helpers/utils.js';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';

const themeSec = createTheme({
  overrides: {
    MuiSelect: {
      root: {
        width: 95,
        height: 60,
        borderRadius: 10,
        border: '1px solid #DFDFDF',
        padding: 10,
        margin: 0,
      },
      outlined: {
        height: 60,
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 6,
        border: '1px solid #DFDFDF',
        padding: 0,
        marginRight: 5,
        marginLeft: 15,
        transform: `rotate(90deg)`,
      },
    },
  },
});

const themeSecMobile = createTheme({
  overrides: {
    MuiSelect: {
      input: {
        border: '0px',
      },
      root: {
        width: 135,
        height: 60,
        borderRadius: 10,
        padding: 10,
        margin: 0,
        border: '0px',
        fontWeight: 'bold',
      },
    },

    MuiIconButton: {
      root: {
        borderRadius: 6,
        padding: 0,
        marginRight: 5,
        border: '1px solid #DFDFDF',
        transform: `rotate(90deg)`,
      },
    },
  },
});

function descendingComparator(a, b, orderBy) {
  const aOrderBy = _.get(a, orderBy)?.toString();
  const bOrderBy = _.get(b, orderBy)?.toString();
  if (aOrderBy?.indexOf('R$') > -1 && bOrderBy?.indexOf('R$') > -1) {
    const newAOrderBy = aOrderBy?.split('R$ ')?.[1].replace('.', '').replace(',', '.');
    const newBOrderBy = bOrderBy?.split('R$ ')?.[1].replace('.', '').replace(',', '.');
    if (parseFloat(newBOrderBy) < parseFloat(newAOrderBy)) {
      return -1;
    }
    if (parseFloat(newBOrderBy) > parseFloat(newAOrderBy)) {
      return 1;
    }
    return 0;
  }
  if (bOrderBy < aOrderBy) {
    return -1;
  }
  if (bOrderBy > aOrderBy) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  if (!array || !Array.isArray(array)) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, columns } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = columns;
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.field}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.field ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.field}
              direction={orderBy === headCell.field ? order : 'asc'}
              onClick={createSortHandler(headCell.field)}>
              {headCell.title}
              {orderBy === headCell.field ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.any,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  toolbar: {
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  container: {
    overflow: 'none',
    padding: theme.spacing(4),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  title: {
    marginRight: theme.spacing(2),
  },
  emptyMessage: {
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  loading: {
    minWidth: '400%',
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
}));

export default function EnhancedTable({
  rows: defaultData,
  columns,
  title,
  onRowClick,
  toolbarSearch,
  searchBarOnChange,
  searchPlaceholder,
  searchDebounce,
  disableSearchIconClick,
  smallSearch,
  dataType,
  filters,
  mobileDropdown,
  onChangeOption,
  mobileTable,
  defaultSelected,
  selectOptions,
  csvData,
  buttonCSV,
  csvFilename,
  customSearch,
  emptyMessage = 'Table is empty',
  setAPIPage,
  setAPILimit,
  countFromAPI,
  loading,
  hideToolBar,
  hideLinesPerPage,
  hideRowPerPageOptions,
  onExportClick,
  defaultRowsPerPage = 5,
}) {
  const classes = useStyles();
  const storageKey = title && !setAPIPage ? `currentPage:${title}` : null;
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('asc');
  const [page, setPage] = useState(() => {
    if (!storageKey) return 0;
    return Number(sessionStorage.getItem(storageKey)) || 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchValue, setSearchValue] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const [optionSelected, setOptionSelected] = useState(defaultSelected || '');

  const buttonRef = useRef();
  useEffect(() => {
    const filteredByName = searchTableValue(searchValue, defaultData, dataType);
    const filteredBySelectors = filterTableData(appliedFilters, filteredByName);
    setFilteredData(filteredBySelectors);
  }, [searchValue, appliedFilters]);

  useEffect(() => {
    setFilteredData(defaultData || []);
  }, [defaultData]);

  useEffect(() => {
    if (storageKey) sessionStorage.setItem(storageKey, page);
  }, [page, storageKey]);

  useEffect(() => {
    if (setAPIPage) return;
    if (filteredData.length === 0) return;
    const maxPage = Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredData.length, rowsPerPage, setAPIPage, page]);

  useEffect(() => {
    if (onChangeOption) onChangeOption(optionSelected);
  }, [optionSelected]);

  const handleSelectChange = (event) => {
    setOptionSelected(event.target.value);
  };
  const filterTableData = (appliedFilters, backupData) => {
    let currentData = [];
    let filtered = [];
    if (appliedFilters.length > 0) {
      currentData = backupData;
      filtered = currentData.filter((item) => {
        const array = [];
        for (let i = 0; i < appliedFilters.length; i++) {
          if (appliedFilters[i].field === 'betweenDates') {
            if (appliedFilters[i].filterFunction === 0) {
              if (
                moment(item.participationStart).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                ) &&
                moment(item.participationEnd).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                )
              ) {
                array.push(true);
              }
            } else if (appliedFilters[i].filterFunction === 1) {
              if (
                moment(item.action.participationStart).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                ) &&
                moment(item.action.participationEnd).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                )
              ) {
                array.push(true);
              }
            } else if (appliedFilters[i].filterFunction === 2) {
              if (
                moment(item.start_date).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                )
              ) {
                array.push(true);
              }
            } else if (appliedFilters[i].filterFunction === 3) {
              if (
                moment(item.createdAt).isBetween(
                  appliedFilters[i].startDate,
                  appliedFilters[i].endDate,
                  undefined,
                  []
                )
              ) {
                array.push(true);
              }
            }
          } else if (
            appliedFilters[i].value[0] <= item[appliedFilters[i].field] &&
            item[appliedFilters[i].field] <= appliedFilters[i].value[1]
          ) {
            array.push(true);
          }
        }
        if (array.length === appliedFilters.length) return true;
        return false;
      });
    } else {
      filtered = backupData;
    }

    return filtered;
  };

  const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
    const handleFirstPageClick = (event) => {
      onPageChange(event, 0);
    };

    const handlePrevPageClick = (event) => {
      onPageChange(event, page - 1);
    };

    const handleNextPageClick = (event) => {
      onPageChange(event, page + 1);
    };

    const handleLastPageClick = (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="Primeira Página">
          <span>
            <IconButton onClick={handleFirstPageClick} disabled={page === 0}>
              <FirstPage />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Página Anterior">
          <span>
            <IconButton onClick={handlePrevPageClick} disabled={page === 0}>
              <KeyboardArrowLeft />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Próxima Página">
          <span>
            <IconButton
              onClick={handleNextPageClick}
              disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
              <KeyboardArrowRight />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Última Página">
          <span>
            <IconButton
              onClick={handleLastPageClick}
              disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
              <LastPage />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (setAPIPage) setAPIPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (setAPILimit) {
      setAPIPage(1);
      setAPILimit(parseInt(event.target.value, 10));
    }
  };
  const handleClick = () => {
    setAnchorEl(true);
  };

  const handleCsvDownload = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!csvData) return;

    const content = typeof csvData === 'string' ? csvData : '';
    if (!content.trim()) return;

    const safeName = String(csvFilename || 'Relatorio.csv')
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
    const filename = safeName.toLowerCase().endsWith('.csv') ? safeName : `${safeName}.csv`;
    const blob = new Blob(['\ufeff', content], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        {!hideToolBar && (
          <Toolbar className={clsx(classes.toolbar)}>
            <Box display="flex" justifyContent="start" alignItems="center">
              <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                {title}
              </Typography>
              {selectOptions ? (
                selectOptions.length > 0 ? (
                  <ThemeProvider theme={mobileTable && mobileDropdown ? themeSecMobile : themeSec}>
                    <Select
                      disableUnderline={mobileDropdown && mobileTable}
                      variant={mobileDropdown && mobileTable ? 'standard' : 'outlined'}
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      value={optionSelected}
                      onChange={handleSelectChange}>
                      {selectOptions.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </ThemeProvider>
                ) : null
              ) : null}
            </Box>
            <Box display="flex" justifyContent="end" alignItems="center">
              {buttonCSV && csvData ? (
                <Box display="flex" justifyContent="flex-end" alignItems="center" height="100%">
                  <IconButton onClick={handleCsvDownload} aria-label="Baixar CSV">
                    <GetApp
                      style={{
                        fontSize: 25,
                        marginRight: 10,
                      }}
                    />
                  </IconButton>
                </Box>
              ) : null}
              {buttonCSV && onExportClick && (
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  height="100%"
                  onClick={onExportClick}
                  style={{ cursor: 'pointer' }}>
                  <GetApp
                    style={{
                      fontSize: 25,
                      marginRight: 10,
                    }}
                  />
                </Box>
              )}
              {toolbarSearch ? (
                <SearchBar
                  setShowSearchBar={setShowSearchBar}
                  showSearchBar={showSearchBar}
                  placeholder={searchPlaceholder || 'Pesquisar'}
                  onPageChange={handleChangePage}
                  small={smallSearch}
                  setSearchValue={setSearchValue}
                  defaultValue={searchValue}
                  searchBarOnChange={searchBarOnChange}
                  disableSearchIconClick={disableSearchIconClick}
                  searchDebounce={searchDebounce}
                />
              ) : null}
              {customSearch || null}
              {filters && filters.length > 0 ? (
                <ThemeProvider theme={mobileDropdown && mobileTable ? themeSecMobile : themeSec}>
                  <IconButton
                    ref={buttonRef}
                    disableFocusRipple
                    disableRipple
                    onClick={handleClick}>
                    <TuneIcon />
                  </IconButton>
                  <MenuFiltersOptions
                    filterOptions={filters}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    open={Boolean(anchorEl)}
                    buttonRef={() => buttonRef.current}
                    setAnchorEl={setAnchorEl}
                  />
                </ThemeProvider>
              ) : null}
            </Box>
          </Toolbar>
        )}
        <TableContainer className={classes.container}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table">
            <EnhancedTableHead
              classes={classes}
              order={order}
              columns={columns}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={filteredData?.length}
            />
            {loading && loading === 'pending' ? (
              <Box className={classes.loading}>
                <CircularProgress />
              </Box>
            ) : (
              <TableBody>
                {stableSort(filteredData, getComparator(order, orderBy))
                  .slice(
                    setAPIPage ? 0 : page * rowsPerPage,
                    setAPIPage ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow hover tabIndex={-1} key={index}>
                        {columns.map((item, index2) => (
                          <TableCell
                            className={classes.row}
                            key={index2}
                            onClick={(e) =>
                              !item.hasOnClick && onRowClick ? onRowClick(e, row) : null
                            }
                            padding="normal"
                            component="th"
                            id={labelId}
                            scope="row">
                            {!item.render && _.get(row, item.field) ? _.get(row, item.field) : null}
                            {item.render ? item?.render(row) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          labelRowsPerPage={hideLinesPerPage ? '' : 'Linhas por página'}
          rowsPerPageOptions={hideRowPerPageOptions ? [''] : [5, 10, 25]}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          count={countFromAPI !== undefined ? countFromAPI : filteredData?.length || 0}
          labelDisplayedRows={({ from, to, count }) => `${from} - ${to} de ${count}`}
          page={page}
          onPageChange={handleChangePage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </div>
  );
}

EnhancedTable.propTypes = {
  buttonCSV: PropTypes.any,
  columns: PropTypes.array,
  countFromAPI: PropTypes.number,
  csvData: PropTypes.any,
  csvFilename: PropTypes.string,
  customSearch: PropTypes.any,
  dataType: PropTypes.any,
  defaultSelected: PropTypes.string,
  defaultRowsPerPage: PropTypes.number,
  emptyMessage: PropTypes.string,
  filters: PropTypes.shape({
    length: PropTypes.number,
  }),
  loading: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  mobileDropdown: PropTypes.any,
  mobileTable: PropTypes.any,
  onChangeOption: PropTypes.func,
  onRowClick: PropTypes.func,
  rows: PropTypes.any,
  searchPlaceholder: PropTypes.string,
  searchDebounce: PropTypes.number,
  selectOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  setAPILimit: PropTypes.func,
  setAPIPage: PropTypes.func,
  smallSearch: PropTypes.any,
  title: PropTypes.any,
  toolbarSearch: PropTypes.any,
};
